/**
 * The questionnaire.
 *
 * Design decisions worth knowing:
 *
 *   * ONE QUESTION AT A TIME on phones, a grouped list on desktop. A 36-question
 *     form rendered as one scroll is where compliance assessments go to die.
 *   * ANSWERS SAVE AS YOU GO. There is no "save" button for an answer, because
 *     losing half an hour of assessment work to a closed tab is unforgivable.
 *   * PROGRESS IS ALWAYS VISIBLE, so the end is in sight.
 *   * FINALISING IS A DELIBERATE, EXPLAINED ACT. The dialog states exactly what
 *     becomes irreversible and how many gaps it will create, and requires an
 *     explicit acknowledgement — matching the server, which refuses without it.
 */

import { useState, useMemo, useId, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { SUBMISSION_COOLDOWN_DAYS } from '@opencomply/shared';
import {
  Card, Button, Pill, Modal, Skeleton, ErrorNotice, ProgressBar, TONE_FOR_STATUS,
} from '../components/ui';
import { cn } from '../lib/cn';
import { IconChevron } from '../components/icons';
import { EvidencePanel } from '../components/EvidencePanel';
import { ControlLeverage } from '../components/ControlLeverage';
import { EvidenceReuse } from '../components/EvidenceReuse';
import { SoaAssessmentPanel } from '../components/SoaAssessmentPanel';
import {
  EvidenceExpiryAlert, ControlEvidenceWarning, lifecycleAlertLevel, useLifecycleStrings,
} from '../components/EvidenceLifecycle';
import type { EvidenceLifecycleSummary } from '@opencomply/shared';

interface Option { id: string; label: string; compliance: string; score: number; requiresDetail: boolean }
interface Control { id: string; code: string | null; title: string; description: string | null; evidenceGuidance: string | null; options: Array<{ optionId: string }> }
interface Question {
  id: string; code: string; text: string; guidance: string | null;
  evidenceGuidance: string | null; type: string;
  options: Option[]; controls: Control[];
}
interface Requirement {
  id: string; code: string; title: string; description: string | null; level: string;
  questions: Question[]; children: Requirement[];
}
interface Answer {
  id: string; questionId: string; optionId: string | null; text: string | null;
  controlEvaluations: Array<{ id: string; controlId: string | null; status: string }>;
  /**
   * Evidence validity, rolled up by the server for EVERY answer.
   *
   * Carried on the answer rather than fetched per question because the whole
   * point is to know the verdict is unsupported at the moment the screen is
   * drawn — not after someone opens the evidence panel and reads the dates.
   */
  evidenceLifecycle?: EvidenceLifecycleSummary;
}
interface Submission {
  id: string; status: string; answeredCount: number; totalCount: number;
  complianceRate: number; finalizedAt: string | null;
  /** How many times this finalised submission has been reopened for editing. */
  reopenCount?: number;
  reopenedAt?: string | null;
  framework: { id: string; key: string; name: string; version: string };
  answers: Answer[];
  /** The server's configured warning window, so copy never hard-codes 30. */
  evidenceWarningDays?: number;
}

const CONTROL_STATUSES = ['APPLIED', 'NOT_APPLIED', 'NOT_APPLICABLE'] as const;

/** Controls the chosen answer triggers: those with no `options`, or listing this answer's option. */
function relevantControls(controls: Control[], answer: Answer): Control[] {
  return controls.filter(
    (c) => c.options.length === 0 || c.options.some((o) => o.optionId === answer.optionId),
  );
}

// ---------------------------------------------------------------------------
// Progressive disclosure
// ---------------------------------------------------------------------------

type SectionKey = 'guidance' | 'evidence' | 'controls';
type OpenState = Record<SectionKey, boolean>;
const ALL_CLOSED: OpenState = { guidance: false, evidence: false, controls: false };
const storageKey = (questionId: string) => `oc.assessment.details.${questionId}`;

/** Remembered open sections for one question. Storage can throw (private mode,
 *  blocked site data), so every access is guarded and the default is collapsed. */
function readOpenState(questionId: string): OpenState {
  try {
    const raw = window.localStorage.getItem(storageKey(questionId));
    if (!raw) return ALL_CLOSED;
    const parsed = JSON.parse(raw) as Partial<OpenState>;
    return {
      guidance: parsed.guidance === true,
      evidence: parsed.evidence === true,
      controls: parsed.controls === true,
    };
  } catch {
    return ALL_CLOSED;
  }
}

function writeOpenState(questionId: string, state: OpenState): void {
  try {
    if (state.guidance || state.evidence || state.controls) {
      window.localStorage.setItem(storageKey(questionId), JSON.stringify(state));
    } else {
      window.localStorage.removeItem(storageKey(questionId));
    }
  } catch {
    /* Persistence is a convenience; the page works without it. */
  }
}

/**
 * One collapsible group. Same look as the public questionnaire's "What this
 * question means" expander (brand-coloured link, left rule on the content), but
 * a controlled button + region so the open state can be remembered.
 *
 * The body is mounted the first time it opens and then only hidden, so a
 * half-finished upload or an in-flight request is not lost by collapsing, while
 * a section nobody opens never fetches anything.
 */
function Disclosure({ label, hint, open, onToggle, children }: {
  label: string;
  /** Short, always-visible cue next to the label (e.g. "2 to review"). */
  hint?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const baseId = useId();
  const buttonId = `${baseId}-btn`;
  const regionId = `${baseId}-region`;
  const everOpened = useRef(open);
  if (open) everOpened.current = true;

  return (
    <div>
      <button
        type="button"
        id={buttonId}
        aria-expanded={open}
        aria-controls={regionId}
        onClick={onToggle}
        className="group flex w-full items-center gap-2 min-h-[44px] sm:min-h-[36px] rounded-lg
                   text-left text-sm font-medium text-brand hover:underline"
      >
        <IconChevron
          aria-hidden="true"
          className={cn(
            'w-4 h-4 shrink-0 transition-transform motion-reduce:transition-none',
            open && 'rotate-90',
          )}
        />
        <span>{label}</span>
        {hint && <span className="text-xs font-normal text-ink-muted">{hint}</span>}
      </button>
      <div
        id={regionId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        className="mt-1 mb-2 ml-2 border-l-2 border-brand/30 pl-3"
      >
        {everOpened.current && children}
      </div>
    </div>
  );
}

/**
 * Secondary detail for one question, folded away so the first view is just the
 * question and its options. Nothing here is removed or changed in behaviour —
 * it is the same guidance, evidence, reuse, controls and leverage as before.
 */
function QuestionDetails({ question, answer, submissionId, readOnly, onSetControl }: {
  question: Question;
  answer: Answer | undefined;
  submissionId: string;
  readOnly: boolean;
  onSetControl: (controlId: string, status: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<OpenState>(() => readOpenState(question.id));

  const update = (next: OpenState) => {
    setOpen(next);
    writeOpenState(question.id, next);
  };
  const toggle = (key: SectionKey) => update({ ...open, [key]: !open[key] });

  const relevant = answer ? relevantControls(question.controls, answer) : [];
  const unreviewed = answer
    ? relevant.filter(
        (c) => !answer.controlEvaluations.some((e) => e.controlId === c.id && e.status !== 'NOT_REVIEWED'),
      ).length
    : 0;
  const controlsHint = relevant.length > 0
    ? unreviewed > 0
      ? t('assessment.controlsHintToReview', '{{total}} · {{count}} to review', { total: relevant.length, count: unreviewed })
      : `${relevant.length}`
    : undefined;

  const allOpen = open.guidance && open.evidence && open.controls;
  const hasGuidance = !!question.guidance;

  return (
    <section
      aria-label={t('assessment.moreDetail', 'More about this question')}
      className="mt-5 pt-3 border-t border-line"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-ink-muted uppercase tracking-wide">
          {t('assessment.moreDetail', 'More about this question')}
        </p>
        <button
          type="button"
          onClick={() => update(allOpen ? ALL_CLOSED : { guidance: true, evidence: true, controls: true })}
          className="text-xs text-brand hover:underline min-h-[32px] px-1 rounded"
        >
          {allOpen ? t('assessment.collapseAll', 'Collapse all') : t('assessment.expandAll', 'Expand all')}
        </button>
      </div>

      {hasGuidance && (
        <Disclosure
          label={t('assessment.guidanceSection', 'What this means')}
          open={open.guidance}
          onToggle={() => toggle('guidance')}
        >
          <p className="text-sm text-ink-secondary leading-relaxed whitespace-pre-wrap py-1">
            {question.guidance}
          </p>
        </Disclosure>
      )}

      <Disclosure
        label={t('assessment.evidenceSection', 'Evidence')}
        open={open.evidence}
        onToggle={() => toggle('evidence')}
      >
        <div className="space-y-4 py-1">
          {question.evidenceGuidance && (
            <div>
              <p className="text-xs font-medium text-ink-secondary mb-1">
                {t('assessment.evidenceGuidance')}
              </p>
              <p className="text-sm text-ink-secondary leading-relaxed">{question.evidenceGuidance}</p>
            </div>
          )}

          {/* "You already proved this": renders nothing unless a catalogue
              control behind this question is already SATISFIED here. */}
          <EvidenceReuse
            questionId={question.id}
            submissionId={submissionId}
            answeredOptionId={answer?.optionId ?? null}
            readOnly={readOnly}
          />

          {/* Evidence hangs off the answer row, so until an answer exists there
              is nothing to attach it TO. `readOnly` covers FINALIZED: add/remove
              are hidden while download and integrity verification remain. Keyed
              by answer so upload progress never leaks between questions. */}
          {answer ? (
            <EvidencePanel key={answer.id} answerId={answer.id} readOnly={readOnly} />
          ) : readOnly ? null : (
            <p className="text-sm text-ink-muted">
              {t('evidence.answerFirst', {
                defaultValue: 'Choose an answer first, then you can attach evidence to it.',
              })}
            </p>
          )}
        </div>
      </Disclosure>

      <Disclosure
        label={t('assessment.controlsSection', 'Controls')}
        hint={controlsHint}
        open={open.controls}
        onToggle={() => toggle('controls')}
      >
        <div className="space-y-4 py-1">
          {answer && question.controls.length > 0 && (
            <SuggestedControls
              controls={question.controls}
              answer={answer}
              readOnly={readOnly}
              /* Only the strongest case marks the individual controls: when
                 SOME evidence still holds, the control may well be fine and a
                 red flag on it would be a false alarm of the opposite kind. */
              evidenceUnsupported={lifecycleAlertLevel(answer.evidenceLifecycle) === 'critical'}
              onSet={onSetControl}
            />
          )}
          {/* Deliberately independent of the answer: the leverage belongs to
              the QUESTION. Knowing it also satisfies other frameworks matters
              before answering, not only after. */}
          <ControlLeverage questionId={question.id} readOnly={readOnly} />
        </div>
      </Disclosure>
    </section>
  );
}

export function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { tl } = useLifecycleStrings();
  const { allows } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [activeIndex, setActiveIndex] = useState(0);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenConfirmed, setReopenConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: submission, isLoading } = useQuery({
    queryKey: ['submission', id],
    queryFn: () => api.get<Submission>(`/submissions/${id}`),
    enabled: !!id,
  });

  const { data: tree } = useQuery({
    queryKey: ['framework', submission?.framework.key],
    queryFn: () => api.get<{ requirements: Requirement[] }>(`/frameworks/${submission!.framework.key}`),
    enabled: !!submission,
  });

  /** Flatten the requirement tree into an ordered question list with context. */
  const questions = useMemo(() => {
    if (!tree) return [];
    const out: Array<{ question: Question; sectionCode: string; sectionTitle: string }> = [];
    const walk = (node: Requirement, section: { code: string; title: string }) => {
      const here = node.questions.length > 0 ? { code: node.code, title: node.title } : section;
      for (const question of node.questions) {
        out.push({ question, sectionCode: here.code, sectionTitle: here.title });
      }
      for (const child of node.children) walk(child, here);
    };
    for (const root of tree.requirements) walk(root, { code: root.code, title: root.title });
    return out;
  }, [tree]);

  const answersByQuestion = useMemo(() => {
    const map = new Map<string, Answer>();
    for (const a of submission?.answers ?? []) map.set(a.questionId, a);
    return map;
  }, [submission]);

  const saveAnswer = useMutation({
    mutationFn: (body: { questionId: string; optionId?: string | null; text?: string | null }) =>
      api.put(`/submissions/${id}/answers`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['submission', id] });
      setError(null);
    },
    onError: (err) => setError(describeError(err)),
  });

  const saveControl = useMutation({
    mutationFn: (body: { answerId: string; controlId: string; status: string }) =>
      api.put(`/submissions/answers/${body.answerId}/controls`, {
        controlId: body.controlId,
        status: body.status,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['submission', id] }),
    onError: (err) => setError(describeError(err)),
  });

  const { data: preview } = useQuery({
    queryKey: ['finalize-preview', id],
    queryFn: () => api.get<{
      answeredCount: number; totalCount: number;
      unansweredRequired: number; unreviewedControls: number; projectedGaps: number;
    }>(`/submissions/${id}/finalize-preview`),
    enabled: finalizeOpen,
  });

  const finalize = useMutation({
    mutationFn: () => api.post<{ gapsCreated: number }>(`/submissions/${id}/finalize`, { acknowledged: true }),
    onSuccess: () => {
      setFinalizeOpen(false);
      void queryClient.invalidateQueries();
      navigate('/gaps');
    },
    onError: (err) => setError(describeError(err)),
  });

  const reopen = useMutation({
    mutationFn: () => api.post<{ reopenCount: number }>(`/submissions/${id}/reopen`, { acknowledge: true }),
    onSuccess: () => {
      setReopenOpen(false);
      setReopenConfirmed(false);
      // Re-fetch: the submission becomes editable again and the header flips.
      void queryClient.invalidateQueries();
    },
    onError: (err) => setError(describeError(err)),
  });

  if (isLoading || !submission) {
    return (
      <div className="oc-page space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-80" />
      </div>
    );
  }

  const readOnly = submission.status === 'FINALIZED' || !allows('submission.answer');
  const current = questions[activeIndex];
  const currentAnswer = current ? answersByQuestion.get(current.question.id) : undefined;
  const progress = submission.totalCount > 0
    ? Math.round((submission.answeredCount / submission.totalCount) * 100)
    : 0;

  /**
   * How much of this assessment is standing on lapsed evidence.
   *
   * Counted across the WHOLE submission, not just the open question. The
   * compliance percentage in the header is computed from the stored verdicts
   * and knows nothing about expiry — so without this count, the one number
   * everybody reads stays confidently green while the documents under it rot.
   * Stating the size of the problem next to that number is the point.
   */
  const evidenceRisk = (() => {
    let expired = 0;
    let expiringSoon = 0;
    for (const answer of submission.answers) {
      const level = lifecycleAlertLevel(answer.evidenceLifecycle);
      // 'serious' is "some expired, some still hold" — counted with the expired
      // ones here, because at assessment level the thing worth surfacing is
      // "this answer needs looking at", not the gradation.
      if (level === 'critical' || level === 'serious') expired += 1;
      else if (level === 'warning') expiringSoon += 1;
    }
    return { expired, expiringSoon };
  })();

  return (
    <div className="oc-page space-y-4 animate-fade-in pb-4">
      {/* Header + progress, sticky so the finish line stays visible while scrolling. */}
      <div className="sticky top-14 lg:top-0 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3
                      bg-surface-sunken/95 backdrop-blur">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <Link to="/assessments" className="text-xs text-ink-muted hover:text-ink-secondary">
              ← {t('assessment.title')}
            </Link>
            <h1 className="text-lg sm:text-2xl font-semibold text-ink truncate">
              {submission.framework.name}
            </h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Pill tone={TONE_FOR_STATUS[submission.status] ?? 'neutral'}>
              {/* Dynamic key: DRAFT/FINALIZED resolve from the bundle; REOPENED
                  falls back to an inline default until the key is merged. */}
              {t(`status.${submission.status}`, {
                defaultValue: submission.status === 'REOPENED' ? 'Reopened' : submission.status,
              })}
            </Pill>
            {!readOnly && allows('submission.finalize') && (
              <Button variant="primary" size="sm" onClick={() => setFinalizeOpen(true)}>
                {t('assessment.finalize')}
              </Button>
            )}
            {submission.status === 'FINALIZED' && allows('submission.reopen') && (
              <Button size="sm" onClick={() => setReopenOpen(true)}>
                {t('assessment.reopen', 'Reopen to edit')}
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-muted whitespace-nowrap">
            {t('assessment.progress', {
              answered: submission.answeredCount,
              total: submission.totalCount,
            })}
          </span>
          <div className="flex-1"><ProgressBar value={progress} label="Assessment progress" /></div>
        </div>
      </div>

      {error && <ErrorNotice message={error} />}

      {/* --- Finalised record: steer towards a new assessment, offer reopen ---
          A finalised submission is a point-in-time compliance record. Editing it
          is possible but discouraged; the non-alarmist-but-serious wording says
          so and points at the recommended path (start a new assessment). The
          actual reopen lives behind the confirm modal, not this banner. */}
      {submission.status === 'FINALIZED' && allows('submission.reopen') && (
        <div role="status" className="rounded-xl border border-warning/50 bg-warning/[0.09] px-3 py-2.5">
          <p className="text-sm font-semibold text-warning">
            {t('assessment.reopenBannerTitle', 'This assessment is finalised')}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-ink-secondary">
            {t(
              'assessment.reopenBannerBody',
              'A finalised assessment is a record of what was true when you completed it. You can reopen it to change answers, but the recommended path is to start a new assessment instead. Reopening may change your gaps and any remediation plans built on them.',
            )}
            {(submission.reopenCount ?? 0) > 0 &&
              ` ${t('assessment.reopenCount', 'This assessment has been reopened {{count}} time(s) before.', { count: submission.reopenCount ?? 0 })}`}
          </p>
        </div>
      )}

      {/* --- Editing a reopened record ---
          While REOPENED, the screen is editable again but the user should not
          lose sight of what they are doing: they are altering a record that was
          already finalised, and they will need to re-finalise to lock it. */}
      {submission.status === 'REOPENED' && (
        <div role="alert" className="rounded-xl border border-critical/50 bg-critical/[0.09] px-3 py-2.5">
          <p className="text-sm font-semibold text-critical">
            {t('assessment.reopenedTitle', 'You are editing a previously finalised assessment')}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-ink-secondary">
            {t(
              'assessment.reopenedBody',
              'Your changes are not a new assessment — they revise the finalised record. When you finalise again, gaps for answers you have fixed will be closed, and any remediation plans built on those gaps may need review.',
            )}
          </p>
        </div>
      )}

      {/* --- Assessment-wide evidence decay ---
          Placed immediately under the compliance progress bar, because that bar
          is the claim this contradicts. Expired is announced; expiring-soon is
          a status update. */}
      {evidenceRisk.expired > 0 ? (
        <div role="alert" className="rounded-xl border border-critical/50 bg-critical/[0.09] px-3 py-2.5">
          <p className="text-sm font-semibold text-critical">
            {tl('evidenceLifecycle.submissionAlertTitle', { count: evidenceRisk.expired })}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-ink-secondary">
            {tl('evidenceLifecycle.submissionAlertBody')}
          </p>
        </div>
      ) : evidenceRisk.expiringSoon > 0 ? (
        <div role="status" className="rounded-xl border border-warning/50 bg-warning/[0.09] px-3 py-2.5">
          <p className="text-sm font-semibold text-warning">
            {tl('evidenceLifecycle.submissionExpiringTitle', {
              count: evidenceRisk.expiringSoon,
              days: submission.evidenceWarningDays ?? 30,
            })}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-ink-secondary">
            {tl('evidenceLifecycle.submissionExpiringBody')}
          </p>
        </div>
      ) : null}

      {/* Question navigator: a dense grid of squares, one per question, coloured
          by the compliance of its answer. Doubles as an overview and a jump
          control — far more useful than "Question 14 of 36" alone.

          A square whose answer rests on expired evidence is additionally
          OUTLINED, and its accessible label and tooltip say so in words. The
          outline is a shape change, not a second colour: the fill already
          encodes compliance, and overloading that fill would mean a green
          square turning red for a reason unrelated to what green means here.
          A green square with a red outline reads correctly — "assessed as
          compliant, evidence no longer supports it" — which is exactly the
          false green this is meant to expose. */}
      <Card className="!p-3">
        <div className="flex flex-wrap gap-1.5">
          {questions.map((entry, index) => {
            const answer = answersByQuestion.get(entry.question.id);
            const option = entry.question.options.find((o) => o.id === answer?.optionId);
            const tone = option ? TONE_FOR_STATUS[option.compliance] : undefined;
            const level = lifecycleAlertLevel(answer?.evidenceLifecycle);
            const evidenceNote =
              level === 'critical' || level === 'serious'
                ? tl('evidenceLifecycle.navExpired')
                : level === 'warning'
                  ? tl('evidenceLifecycle.navExpiringSoon')
                  : null;
            const label =
              `${entry.question.code}${option ? `: ${option.label}` : ''}` +
              (evidenceNote ? ` — ${evidenceNote}` : '');
            return (
              <button
                key={entry.question.id}
                onClick={() => setActiveIndex(index)}
                aria-label={label}
                aria-current={index === activeIndex}
                title={label}
                className={cn(
                  'w-7 h-7 rounded-md text-[10px] font-medium transition-all',
                  'border',
                  index === activeIndex && 'ring-2 ring-brand ring-offset-1 ring-offset-surface',
                  tone === 'good' && 'bg-good/15 border-good/40 text-good',
                  tone === 'warning' && 'bg-warning/20 border-warning/40 text-warning',
                  tone === 'critical' && 'bg-critical/15 border-critical/40 text-critical',
                  tone === 'neutral' && 'bg-ink/10 border-line text-ink-muted',
                  !tone && 'bg-surface-sunken border-line text-ink-muted',
                  // Outline wins over the active ring only when the square is
                  // not the current one, so the "you are here" cue is never lost.
                  (level === 'critical' || level === 'serious') &&
                    index !== activeIndex &&
                    'outline outline-2 outline-offset-1 outline-critical',
                  level === 'warning' &&
                    index !== activeIndex &&
                    'outline outline-2 outline-offset-1 outline-warning',
                )}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
      </Card>

      {/* The active question */}
      {current && (
        <Card>
          <p className="text-xs font-medium text-brand mb-1">
            {current.sectionCode} · {current.sectionTitle}
          </p>
          <h2 className="text-base sm:text-lg font-medium text-ink leading-relaxed">
            {current.question.text}
          </h2>
          <p className="text-xs text-ink-muted mt-1 font-mono">{current.question.code}</p>


          {/* --- Evidence validity, BEFORE the verdict it contradicts ---
              The failure being prevented is someone reading a green "Compliant"
              pill and moving on. A warning placed after the thing it
              contradicts has already lost, so this sits above the options, the
              controls and the evidence panel alike.

              The stored verdict itself is deliberately NOT altered. It records
              what was assessed at a point in time; recolouring it would claim
              the assessment said something it did not, would destroy the record
              an auditor came to read, and would have to be undone the day the
              certificate is renewed. The expiry is stated beside it instead. */}
          {currentAnswer?.evidenceLifecycle && (
            <div className="mt-4">
              <EvidenceExpiryAlert
                summary={currentAnswer.evidenceLifecycle}
                warningDays={submission.evidenceWarningDays}
              />
            </div>
          )}


          {/* Options */}
          <fieldset className="mt-5" disabled={readOnly}>
            <legend className="sr-only">{current.question.text}</legend>
            <div className="space-y-2">
              {current.question.options.map((option) => {
                const selected = currentAnswer?.optionId === option.id;
                return (
                  <label
                    key={option.id}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                      'min-h-[3rem]',
                      selected
                        ? 'border-brand bg-brand/[0.06]'
                        : 'border-line hover:bg-surface-sunken',
                      readOnly && 'cursor-default opacity-90',
                    )}
                  >
                    <input
                      type="radio"
                      name={current.question.id}
                      className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))] shrink-0"
                      checked={selected}
                      disabled={readOnly}
                      onChange={() =>
                        saveAnswer.mutate({
                          questionId: current.question.id,
                          optionId: option.id,
                          // Detail is preserved when re-selecting, cleared when the
                          // new option does not need one.
                          text: option.requiresDetail ? (currentAnswer?.text ?? '') : null,
                        })
                      }
                    />
                    <span className="min-w-0 flex-1">
                      <span className="text-sm text-ink">{option.label}</span>
                      {option.requiresDetail && (
                        <span className="block text-xs text-ink-muted mt-0.5">
                          {t('assessment.explainPartial')}
                        </span>
                      )}
                    </span>
                    <Pill tone={TONE_FOR_STATUS[option.compliance] ?? 'neutral'} className="shrink-0">
                      {t(`status.${option.compliance}`)}
                    </Pill>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {/* Explanation, required by some options */}
          {(() => {
            const selected = current.question.options.find((o) => o.id === currentAnswer?.optionId);
            if (!selected?.requiresDetail && !currentAnswer?.text) return null;
            return (
              <div className="mt-4">
                <label className="oc-label" htmlFor="detail">{t('assessment.explainPartial')}</label>
                <textarea
                  id="detail"
                  className="oc-input min-h-[5rem] py-2 resize-y"
                  placeholder={t('assessment.explainPlaceholder')}
                  defaultValue={currentAnswer?.text ?? ''}
                  disabled={readOnly}
                  // Saved on blur rather than per keystroke: one request when the
                  // user finishes, not forty while they type.
                  onBlur={(e) => {
                    if (readOnly) return;
                    if (e.target.value === (currentAnswer?.text ?? '')) return;
                    saveAnswer.mutate({
                      questionId: current.question.id,
                      optionId: currentAnswer?.optionId ?? null,
                      text: e.target.value,
                    });
                  }}
                />
              </div>
            );
          })()}


          {/*
            THE STATEMENT OF APPLICABILITY, BUILT HERE.

            ISO 27001 seeds Annex A as one question per theme (ISO-A.5 … A.8),
            and this is the moment the user is already thinking about that
            theme. Clause 6.1.3(d) needs a decision on each of its controls, and
            asking for it here — in plain questions about their organisation —
            is the difference between an SoA that gets written and a 93-row
            screen somewhere else that does not.

            Gated on the framework key as well as the question code: another
            framework could perfectly well seed a question coded "ISO-A.5", and
            the SoA is an ISO 27001 document.
          */}
          {submission.framework.key === 'iso27001'
            && /^ISO-A\.\d+$/.test(current.question.code) && (
            <div className="mt-5 pt-4 border-t border-line">
              <SoaAssessmentPanel
                theme={current.question.code.replace(/^ISO-/, '')}
                readOnly={readOnly}
              />
            </div>
          )}

          {/* Everything helpful-but-secondary lives behind disclosures, collapsed
              by default and remembered per question. The answer options above
              and the expiry alert stay on screen regardless. Keyed by question
              so each question restores its own remembered state. */}
          <QuestionDetails
            key={current.question.id}
            question={current.question}
            answer={currentAnswer}
            submissionId={submission.id}
            readOnly={readOnly}
            onSetControl={(controlId, status) => {
              if (currentAnswer) saveControl.mutate({ answerId: currentAnswer.id, controlId, status });
            }}
          />


          {/* Prev / next */}
          <div className="mt-5 pt-4 border-t border-line flex items-center justify-between gap-3">
            <Button
              size="sm"
              disabled={activeIndex === 0}
              onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
            >
              {t('common.previous')}
            </Button>
            <span className="text-xs text-ink-muted tabular-nums">
              {activeIndex + 1} {t('common.of')} {questions.length}
            </span>
            <Button
              size="sm"
              variant="primary"
              disabled={activeIndex >= questions.length - 1}
              onClick={() => setActiveIndex((i) => Math.min(questions.length - 1, i + 1))}
            >
              {t('common.next')}
            </Button>
          </div>
        </Card>
      )}

      {/* Finalisation */}
      <Modal
        open={finalizeOpen}
        onClose={() => { setFinalizeOpen(false); setAcknowledged(false); }}
        title={t('assessment.finalizeTitle')}
        footer={
          <>
            <Button onClick={() => { setFinalizeOpen(false); setAcknowledged(false); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={!acknowledged || finalize.isPending}
              onClick={() => finalize.mutate()}
            >
              {finalize.isPending ? t('common.saving') : t('assessment.finalize')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ErrorNotice message={t('assessment.finalizeWarning', { days: SUBMISSION_COOLDOWN_DAYS })} />

          {preview && (
            <dl className="rounded-xl bg-surface-sunken p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink-secondary">{t('assessment.progress', {
                  answered: preview.answeredCount, total: preview.totalCount,
                })}</dt>
                <dd className="tabular-nums font-medium text-ink">
                  {preview.answeredCount}/{preview.totalCount}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink-secondary">{t('assessment.willCreateGaps', { count: preview.projectedGaps })}</dt>
                <dd className="tabular-nums font-medium text-serious">{preview.projectedGaps}</dd>
              </div>
              {preview.unreviewedControls > 0 && (
                <p className="pt-2 border-t border-line text-xs text-ink-muted">
                  {t('assessment.unreviewedControls', { count: preview.unreviewedControls })}
                </p>
              )}
            </dl>
          )}

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))]"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span className="text-sm text-ink">{t('assessment.acknowledge')}</span>
          </label>
        </div>
      </Modal>

      {/* Reopen — the deliberate, warned reversal of finalisation. */}
      <Modal
        open={reopenOpen}
        onClose={() => { setReopenOpen(false); setReopenConfirmed(false); }}
        title={t('assessment.reopenTitle', 'Reopen this finalised assessment?')}
        footer={
          <>
            <Button onClick={() => { setReopenOpen(false); setReopenConfirmed(false); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={!reopenConfirmed || reopen.isPending}
              onClick={() => reopen.mutate()}
            >
              {reopen.isPending ? t('common.saving') : t('assessment.reopenConfirm', 'Reopen for editing')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ErrorNotice
            message={t(
              'assessment.reopenWarning',
              'We recommend you do NOT do this. A finalised assessment is a record of what was true when you completed it, and reopening it changes that record. The safer path is to start a new assessment, which keeps this one intact for comparison and audit.',
            )}
          />

          <div className="rounded-xl bg-surface-sunken p-4 space-y-2 text-sm text-ink-secondary leading-relaxed">
            <p>
              {t(
                'assessment.reopenConsequences',
                'If you reopen and change your answers, then finalise again:',
              )}
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t('assessment.reopenConseqGaps', 'Answers you have fixed will have their gaps closed automatically.')}</li>
              <li>{t('assessment.reopenConseqPlans', 'Any remediation plans built on those gaps may be left pointing at a closed gap, and will need review.')}</li>
              <li>{t('assessment.reopenConseqNew', 'New shortfalls will create new gaps, as a normal finalisation does.')}</li>
            </ul>
            {(submission.reopenCount ?? 0) > 0 && (
              <p className="pt-2 border-t border-line text-xs text-ink-muted">
                {t('assessment.reopenCount', 'This assessment has been reopened {{count}} time(s) before.', { count: submission.reopenCount ?? 0 })}
              </p>
            )}
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))]"
              checked={reopenConfirmed}
              onChange={(e) => setReopenConfirmed(e.target.checked)}
            />
            <span className="text-sm text-ink">
              {t('assessment.reopenAcknowledge', 'I understand the problems and want to reopen this assessment anyway.')}
            </span>
          </label>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Suggested controls for the chosen answer.
 *
 * A control with no `options` applies to the question generally; one with
 * options appears only when the selected answer is among them. That is what
 * makes the list actionable rather than a wall of every possible safeguard.
 */
function SuggestedControls({ controls, answer, readOnly, evidenceUnsupported, onSet }: {
  controls: Control[];
  answer: Answer;
  readOnly: boolean;
  /**
   * True when every piece of evidence on this answer has expired.
   *
   * Only then is a control's APPLIED status actually unbacked, and only then is
   * it marked. A control carries no evidence of its own in this schema —
   * evidence hangs off the ANSWER — so "the evidence behind this control" means
   * the answer's evidence, and that is what the marker claims. Being precise
   * about that matters: overstating it would make the flag ignorable.
   */
  evidenceUnsupported?: boolean;
  onSet: (controlId: string, status: string) => void;
}) {
  const { t } = useTranslation();

  const relevant = relevantControls(controls, answer);
  if (relevant.length === 0) return null;

  const statusFor = (controlId: string) =>
    answer.controlEvaluations.find((e) => e.controlId === controlId)?.status ?? 'NOT_REVIEWED';

  return (
    <div>
      <p className="text-sm font-medium text-ink-secondary mb-3">
        {t('assessment.suggestedControls')} ({relevant.length})
      </p>
      <ul className="space-y-2">
        {relevant.map((control) => {
          const status = statusFor(control.id);
          return (
            <li key={control.id} className="rounded-xl border border-line p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">
                    {control.code && <span className="font-mono text-xs text-ink-muted mr-2">{control.code}</span>}
                    {control.title}
                  </p>
                  {control.description && (
                    <p className="text-xs text-ink-secondary mt-1 leading-relaxed">{control.description}</p>
                  )}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  <Pill tone={TONE_FOR_STATUS[status] ?? 'neutral'}>
                    {t(`status.${status}`)}
                  </Pill>
                  {/* Only on a control claimed as APPLIED. A control marked NOT
                      APPLIED or NOT APPLICABLE is not relying on the lapsed
                      document, so flagging it would be noise. */}
                  {evidenceUnsupported && status === 'APPLIED' && <ControlEvidenceWarning />}
                </div>
              </div>
              {!readOnly && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {CONTROL_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => onSet(control.id, s)}
                      aria-pressed={status === s}
                      className={cn(
                        'px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                        status === s
                          ? 'border-brand bg-brand/10 text-brand'
                          : 'border-line text-ink-secondary hover:bg-surface-sunken',
                      )}
                    >
                      {t(`status.${s}`)}
                    </button>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
